import { Box, Button, Grid2 as Grid, Grid2, IconButton, TextField, Typography, useTheme } from '@mui/material';
import * as React from 'react';
import { useApi } from '../Api/useApi';
import { UserDto, UsersRycApiClient } from '../Api/ApiServer';
import { fullHeight } from '../Shared/getFullHeight';
import { AddCircle, DeleteOutline, PersonAdd } from '@mui/icons-material';
import CreateUserDialog from './CreateUserDialog';
import ClickableCheckbox from '../Shared/ClickableCheckbox';
import DeleteDialog from '../Shared/DeleteDialog';
import UserProjectAdminSelect from './UserProjectAdminSelect';
import ResetPasswordScene from './ResetPasswordScene';
import AdminResetPassword from './AdminResetPasswordScene';
import NoDataPlaceholder from '../Shared/NoDataPlaceholder';

export interface IAdminSceneProps {
}

export default function AdminScene(props: IAdminSceneProps) {
  const { authorizedApi } = useApi(UsersRycApiClient);
  const [users, setUsers] = React.useState<UserDto[]>([]);
  const [creatingUser, setCreatingUser] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<UserDto | null>(null);
  const [deletingUser, setDeletingUser] = React.useState<UserDto | null>(null);
  const [resetingPassword, setResetingPassword] = React.useState<UserDto | null>(null);
  const theme = useTheme();
  const reloadUsers = React.useCallback(async () => {
    const users = await authorizedApi?.usersList();
    setUsers(users?.users ?? []);
  }, [authorizedApi]);
  const deleteUser = React.useCallback(async (user: UserDto) => {
    if (user.id) {
      await authorizedApi?.deleteUser(user.id);
      reloadUsers();
    }
  }, [authorizedApi, reloadUsers]);
  React.useEffect(() => {
    reloadUsers();
  }, [reloadUsers]);
  return (
    <Box bgcolor={'white'} overflow='auto'>
      <DeleteDialog
        data={deletingUser}
        onDeleteAgree={async (data) => {
          deleteUser(data);
          if (deletingUser?.id === selectedUser?.id) setSelectedUser(null);
          setDeletingUser(null);
        }}
        onClose={(data) => { setDeletingUser(null); }}
        open={deletingUser !== null}
      />
      <CreateUserDialog open={creatingUser} onClose={(refresh) => { setCreatingUser(false); reloadUsers(); }} />
      <AdminResetPassword open={resetingPassword !== null} onClose={() => setResetingPassword(null)} userId={resetingPassword?.id ?? null} />

      <Grid height='calc(100vh - 40px)' overflow='auto' p={3} width='100vw' spacing={2} container>
        <Grid size={{ lg: 4, xl: 4, md: 6, sm: 12 }}>
          <Typography color='secondary' variant='h6'> Seznam uživatelů</Typography>
          <Button variant='contained' fullWidth startIcon={<PersonAdd />} onClick={() => { setCreatingUser(true) }}>Vytvořit uživatele</Button>
          <Box >{users.map((user) => (
            <Box
              display='flex'
              alignItems='center'
              justifyContent={'space-between'}
              width='100%'
              sx={{
                cursor: 'pointer',
                p: 1,
                '&:hover': { backgroundColor: 'grey.200' },
                borderBottom: '1px solid #C3C3C3',
                bgcolor: selectedUser?.id === user.id ? theme.palette.primary
                  .light : 'transparent',
              }}
              onClick={() => setSelectedUser(user)}
              key={user.id}>
              {user.email}
              <IconButton onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDeletingUser(user);
              }}>
                <DeleteOutline />
              </IconButton>
            </Box>
          ))}</Box>
        </Grid>
        {selectedUser && <Grid size={{ lg: 4, xl: 4, md: 6, sm: 12 }}>
          <Typography color='secondary' variant='h6'> Detail uživatele</Typography>
          <Box>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label='Email'
                  value={selectedUser.email ?? ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value } as UserDto)} />

              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label='Společnost' value={selectedUser.companyName ?? ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, companyName: e.target.value } as UserDto)} />

              </Grid2>
              <Grid2 size={{ xs: 4 }}>
                <Button fullWidth variant='contained' onClick={() => setResetingPassword(selectedUser)}>RESET HESLA</Button>
              </Grid2>
              <Grid2 size={{ xs: 4 }}>
                <ClickableCheckbox label='Admin' checked={selectedUser.isAdmin}
                  onChange={(e, ch) => setSelectedUser({ ...selectedUser, isAdmin: ch } as UserDto)} />

              </Grid2>
              <Grid2 size={{ xs: 4 }}>
                <ClickableCheckbox label='Superadmin' checked={selectedUser.isSuperAdmin}
                  onChange={(e, ch) => setSelectedUser({ ...selectedUser, isSuperAdmin: ch } as UserDto)} />
              </Grid2>

            </Grid2>

          </Box>
        </Grid>}
        {selectedUser && <Grid size={{ lg: 4, xl: 4, md: 6, sm: 12 }}>
          <Typography color='secondary' variant='h6'> Práva na projekty</Typography>
          <UserProjectAdminSelect userId={selectedUser.id!} />
        </Grid>}
        {!selectedUser && <Grid size={{ lg: 8, xl: 8, md: 6, sm: 12 }}>
          <NoDataPlaceholder heading='Vyberte uživatele' text='Pro další akce vyberte uživatele' /></Grid>}
      </Grid>
    </Box>
  );
}
